<template>
	<div class="shop-look">
		<div class="shop-look__header" v-if="settings.heading || settings.description">
			<h2 v-if="settings.heading" class="shop-look__heading">{{ settings.heading }}</h2>
			<p v-if="settings.description" class="shop-look__description">{{ settings.description }}</p>
		</div>

		<div class="shop-look__grid">
			<div class="shop-look__item" v-for="(block, idx) in blocks" :key="idx">
				<div class="shop-look__image" v-if="block.image.src !== null">
					<VPic
						:media="{
							res: '--tablet-up',
							alt: 'Shop the look'
						}"
						:mobile_media="block.image"
						:desktop_media="block.image"
					/>
					<span v-if="getAddedCount(idx) > 0" class="shop-look__badge">
						{{ getAddedCount(idx) }} item{{ getAddedCount(idx) > 1 ? 's' : '' }} added
					</span>
				</div>
				<p class="shop-look__error" v-else>Please upload an image</p>

				<button
					ref="toggle"
					class="shop-look__toggle"
					:class="{
						'is-active': activeDrawer === idx,
						'is-closing': closingDrawer === idx
					}"
					:style="getToggleStyle(idx)"
					@click="onToggleClick(idx)"
					aria-label="View products"
				>
					<!-- Plus icon -->
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="shop-look__toggle-icon shop-look__toggle-icon--plus">
						<path d="M10 0V20" stroke="black"/>
						<path d="M20 10L-7.15256e-07 10" stroke="black"/>
					</svg>
					<!-- Close icon -->
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="shop-look__toggle-icon shop-look__toggle-icon--close">
						<path d="M2 2L18 18" stroke="black"/>
						<path d="M18 2L2 18" stroke="black"/>
					</svg>
				</button>

				<!-- Desktop drawer (inside item) -->
				<transition name="slide-up">
					<div v-if="activeDrawer === idx" ref="drawer" class="shop-look__drawer shop-look__drawer--desktop">
						<div class="shop-look__drawer-content">
							<div class="shop-look__products">
								<div v-for="(product, pIdx) in block.products" :key="pIdx" class="shop-look__product">
									<a :href="product.url" class="shop-look__product-image">
										<img :src="product.featured_image" :alt="product.title" loading="lazy" />
									</a>
									<div class="shop-look__product-info">
									<a :href="product.url" class="shop-look__product-title">{{ getProductTitle(product.title) }}</a>
									<p class="shop-look__product-vendor">{{ product.vendor }}</p>
									<div class="shop-look__product-footer">
										<p class="shop-look__product-price">{{ formatPrice(product.price) }}</p>
										<button type="button" class="shop-look__product-add" @click="selectProduct(product)">add</button>
									</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>

		<!-- Mobile drawer (fixed to viewport) -->
		<transition name="slide-up-mobile">
			<div v-if="activeDrawer !== null" class="shop-look__drawer shop-look__drawer--mobile" :class="{ 'has-selected-product': selectedProduct }">
				<button class="shop-look__drawer-close" @click="closeDrawer" aria-label="Close">
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
						<path d="M2 2L18 18" stroke="black"/>
						<path d="M18 2L2 18" stroke="black"/>
					</svg>
				</button>
				<div class="shop-look__drawer-content" :class="{ 'selected-product': selectedProduct }">
					<!-- Product detail view -->
					<div v-if="selectedProduct" class="shop-look__product-detail">
						<h3 class="shop-look__detail-label">Select options</h3>
						<!-- Product images carousel -->
						<div class="shop-look__detail-images carousel" ref="detailCarousel">
							<div class="carousel-track" data-carousel-track>
								<div v-for="(image, imgIdx) in selectedProductImages" :key="imgIdx" class="shop-look__detail-image">
									<img :src="image" :alt="selectedProduct.title" />
								</div>
							</div>
						</div>

						<!-- Product info -->
						<div class="shop-look__detail-info">
							<a :href="selectedProduct.url" class="shop-look__detail-title">{{ getProductTitle(selectedProduct.title) }}</a>
							<p class="shop-look__detail-colour">{{ getProductColour(selectedProduct.title) }}</p>
							<p class="shop-look__detail-vendor">{{ selectedProduct.vendor }}</p>
							<p class="shop-look__detail-price">{{ formatPrice(selectedProduct.price) }}</p>
						</div>

						<!-- Size Options -->
						<div class="shop-look__detail-sizes">
							<button
								v-for="(variant, idx) in selectedProduct.variants"
								:key="idx"
								type="button"
								class="shop-look__detail-size"
								:class="{
									'is-active': selectedVariant && selectedVariant.id === variant.id,
									'sold-out': !variant.available
								}"
								:disabled="!variant.available"
								@click="selectVariant(variant)"
							>
								{{ getVariantSize(variant) }}
							</button>
						</div>

						<!-- Add to Cart Button -->
						<button
							type="button"
							class="shop-look__detail-add-to-cart btn btn-full btn-secondary btn-black"
							:class="{ 'is-loading': isAdding }"
							:disabled="!selectedVariant || isAdding"
							@click="addToCart"
						>
							{{ selectedVariant ? 'add to cart' : 'please choose a size' }}
						</button>
					</div>
					<!-- Product list view -->
					<div v-else class="shop-look__products">
						<div v-for="(product, pIdx) in blocks[activeDrawer].products" :key="pIdx" class="shop-look__product">
							<a :href="product.url" class="shop-look__product-image">
								<img :src="product.featured_image" :alt="product.title" loading="lazy" />
							</a>
							<div class="shop-look__product-info">
								<a :href="product.url" class="shop-look__product-title">{{ getProductTitle(product.title) }}</a>
								<p class="shop-look__product-vendor">{{ product.vendor }}</p>
								<div class="shop-look__product-footer">
									<p class="shop-look__product-price">{{ formatPrice(product.price) }}</p>
									<button type="button" class="shop-look__product-add" :disabled="isLoadingDetail" @click="selectProduct(product)">add</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>

		<!-- Mobile overlay -->
		<transition name="fade">
			<div v-if="activeDrawer !== null" class="shop-look__overlay" @click="closeDrawer"></div>
		</transition>

		<!-- Desktop slideout drawer (slides in from right like mini-cart) -->
		<div class="shop-look__slideout" :class="{ 'is-active': selectedProduct && $screen.large_up }">
			<button class="shop-look__slideout-close" @click="closeSlideout" aria-label="Close">
				<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
					<path d="M2 2L18 18" stroke="black"/>
					<path d="M18 2L2 18" stroke="black"/>
				</svg>
			</button>
			<div v-if="selectedProduct" class="shop-look__slideout-content">
				<h3 class="shop-look__slideout-label">Select options</h3>
				<!-- Product images carousel -->
				<div class="shop-look__detail-images carousel" data-arrows data-pagination-per-slide ref="desktopCarousel">
					<div class="carousel-track" data-carousel-track>
						<div v-for="(image, imgIdx) in selectedProductImages" :key="imgIdx" class="shop-look__detail-image">
							<img :src="image" :alt="selectedProduct.title" />
						</div>
					</div>
				</div>

				<!-- Product info -->
				<div class="shop-look__detail-info">
					<a :href="selectedProduct.url" class="shop-look__detail-title">{{ getProductTitle(selectedProduct.title) }}</a>
					<p class="shop-look__detail-colour">{{ getProductColour(selectedProduct.title) }}</p>
					<p class="shop-look__detail-vendor">{{ selectedProduct.vendor }}</p>
					<p class="shop-look__detail-price">{{ formatPrice(selectedProduct.price) }}</p>
				</div>

				<!-- Size Options -->
				<div class="shop-look__detail-sizes">
					<button
						v-for="(variant, idx) in selectedProduct.variants"
						:key="idx"
						type="button"
						class="shop-look__detail-size"
						:class="{
							'is-active': selectedVariant && selectedVariant.id === variant.id,
							'sold-out': !variant.available
						}"
						:disabled="!variant.available"
						@click="selectVariant(variant)"
					>
						{{ getVariantSize(variant) }}
					</button>
				</div>

				<!-- Add to Cart Button -->
				<button
					type="button"
					class="shop-look__detail-add-to-cart btn btn-full btn-secondary btn-black"
					:class="{ 'is-loading': isAdding }"
					:disabled="!selectedVariant || isAdding"
					@click="addToCart"
				>
					{{ selectedVariant ? 'add to cart' : 'please choose a size' }}
				</button>
			</div>
		</div>

		<!-- Desktop slideout overlay -->
		<transition name="fade">
			<div v-if="selectedProduct && $screen.large_up" class="shop-look__slideout-overlay" @click="closeSlideout"></div>
		</transition>
	</div>
</template>

<script>
import VPic from './VPic'
import { formatMoney } from '@shopify/theme-currency'
import { addItem } from '../../scripts/cart'

export default {
	name: 'ShopLook',

	props: ['settings', 'blocks'],

	components: {
		VPic
	},

	computed: {
		selectedProductImages() {
			if (!this.selectedProduct || !this.selectedProduct.images) {
				return []
			}
			const images = [...this.selectedProduct.images]
			if (images.length > 1) {
				// Move the image at index 1 to the end
				const secondImage = images.splice(1, 1)[0]
				images.push(secondImage)
			}
			return images
		}
	},

	data() {
		return {
			activeDrawer: null,
			closingDrawer: null,
			drawerHeight: 0,
			closingHeight: 0,
			selectedProduct: null,
			selectedVariant: null,
			isAdding: false,
			isLoadingDetail: false,
			addedItemsPerBlock: {}
		}
	},

	methods: {
		onToggleClick(idx) {
			if (this.activeDrawer === idx) {
				// Start closing animation
				this.closingDrawer = idx
				this.closingHeight = this.drawerHeight
				this.activeDrawer = null
				this.drawerHeight = 0
				this.selectedProduct = null
				this.selectedVariant = null

				// Clear closing state after animation
				setTimeout(() => {
					this.closingDrawer = null
					this.closingHeight = 0
				}, 500)
			} else {
				this.activeDrawer = idx
				this.$nextTick(() => {
					this.updateTogglePosition()
				})
			}
		},
		closeDrawer() {
			// If product detail is open, go back to products first
			if (this.selectedProduct) {
				this.selectedProduct = null
				this.selectedVariant = null
				return
			}

			if (this.activeDrawer !== null) {
				this.closingDrawer = this.activeDrawer
				this.closingHeight = this.drawerHeight
				this.activeDrawer = null
				this.drawerHeight = 0
				this.selectedProduct = null
				this.selectedVariant = null

				setTimeout(() => {
					this.closingDrawer = null
					this.closingHeight = 0
				}, 500)
			}
		},

		updateTogglePosition() {
			const drawer = this.$refs.drawer
			if (drawer) {
				const drawerEl = Array.isArray(drawer) ? drawer[0] : drawer
				if (drawerEl) {
					// Wait for images to load before calculating height
					const images = drawerEl.querySelectorAll('img')
					if (images.length === 0) {
						this.drawerHeight = drawerEl.offsetHeight
						return
					}

					let loadedCount = 0
					const checkAllLoaded = () => {
						loadedCount++
						if (loadedCount >= images.length) {
							this.drawerHeight = drawerEl.offsetHeight
						}
					}

					images.forEach(img => {
						if (img.complete) {
							checkAllLoaded()
						} else {
							img.addEventListener('load', checkAllLoaded, { once: true })
							img.addEventListener('error', checkAllLoaded, { once: true })
						}
					})
				}
			}
		},

		getToggleStyle(idx) {
			// When active, position above drawer
			if (this.activeDrawer === idx && this.drawerHeight > 0 && this.$screen.large_up) {
				return {
					bottom: `calc(${this.drawerHeight}px + 2rem)`,
					'--drawer-height': `${this.drawerHeight}px`
				}
			}
			// When closing, animate from elevated position back down
			if (this.closingDrawer === idx && this.closingHeight > 0 && this.$screen.large_up) {
				return {
					'--drawer-height': `${this.closingHeight}px`
				}
			}
			return {}
		},

		formatPrice(price) {
			return formatMoney(price, theme.moneyFormat)
		},

		getProductTitle(title) {
			return title.split(' / ')[0]
		},

		async selectProduct(product) {
			this.isLoadingDetail = true
			this.selectedVariant = null

			// Preload images before showing detail
			await this.preloadImages(product.images)

			this.selectedProduct = product
			this.isLoadingDetail = false

			// Init carousel for the appropriate drawer
			if (this.$screen.large_up) {
				this.initDesktopCarousel()
			} else {
				this.initDetailCarousel()
			}
		},

		initDesktopCarousel() {
			this.$nextTick(() => {
				const carouselEl = this.$refs.desktopCarousel
				if (carouselEl && typeof ScrollCarousel !== 'undefined') {
					new ScrollCarousel(carouselEl).init()
				}
			})
		},

		initDetailCarousel() {
			this.$nextTick(() => {
				const carouselEl = this.$refs.detailCarousel
				if (carouselEl && typeof ScrollCarousel !== 'undefined') {
					new ScrollCarousel(carouselEl).init()
				}
			})
		},

		preloadImages(images) {
			return Promise.all(
				images.map(src => {
					return new Promise((resolve) => {
						const img = new Image()
						img.onload = resolve
						img.onerror = resolve
						img.src = src
					})
				})
			)
		},

		closeSlideout() {
			this.selectedProduct = null
			this.selectedVariant = null
		},

		getProductColour(title) {
			return title.split(' / ')[1] || ''
		},

		getVariantSize(variant) {
			return variant.title.split(' / ')[1] || ''
		},

		selectVariant(variant) {
			if (variant.available) {
				this.selectedVariant = variant
			}
		},

		async addToCart() {
			if (!this.selectedVariant) return

			this.isAdding = true

			const variantId = Number(this.selectedVariant.id)
			let properties = {}

			if (Number(this.selectedVariant.compare_at_price) > Number(this.selectedVariant.price)) {
				properties = {
					_is_sale: 'true'
				}
			}

			try {
				await addItem(variantId, {
					quantity: 1,
					properties: properties
				})

				// Update header cart count
				this.updateCartCount()

				// Track added item for this block
				this.trackAddedItem()

				// Go back to products list
				this.selectedProduct = null
				this.selectedVariant = null
			} catch (error) {
				console.error('Error adding to cart:', error)
				alert('Error adding product to your bag, please try again.')
			} finally {
				this.isAdding = false
			}
		},

		updateCartCount() {
			const cartCountEl = document.querySelector('.cart-item-count')
			if (cartCountEl) {
				const currentCount = parseInt(cartCountEl.textContent) || 0
				cartCountEl.textContent = currentCount + 1
				cartCountEl.parentElement.classList.remove('hidden-count')
			}
		},

		trackAddedItem() {
			if (this.activeDrawer !== null && this.selectedProduct) {
				const blockIdx = this.activeDrawer
				const productId = this.selectedProduct.id

				if (!this.addedItemsPerBlock[blockIdx]) {
					this.$set(this.addedItemsPerBlock, blockIdx, [])
				}

				// Add product ID if not already tracked
				if (!this.addedItemsPerBlock[blockIdx].includes(productId)) {
					this.addedItemsPerBlock[blockIdx].push(productId)
				}
			}
		},

		getAddedCount(blockIdx) {
			return this.addedItemsPerBlock[blockIdx] ? this.addedItemsPerBlock[blockIdx].length : 0
		},

		async syncCartItems() {
			try {
				const response = await fetch('/cart.json')
				const cart = await response.json()
				const cartProductIds = cart.items.map(item => item.product_id)

				// Check each block's products against cart
				this.blocks.forEach((block, blockIdx) => {
					const addedProducts = []
					block.products.forEach(product => {
						if (cartProductIds.includes(product.id)) {
							addedProducts.push(product.id)
						}
					})
					this.$set(this.addedItemsPerBlock, blockIdx, addedProducts)
				})
			} catch (error) {
				console.error('Error syncing cart items:', error)
			}
		}
	},

	mounted() {
		this.syncCartItems()

		// Listen for cart changes (items added/removed from cart drawer)
		$(document.body).on('afterChangeItem.ajaxCart.shopLook', () => {
			this.syncCartItems()
		})
	},

	beforeDestroy() {
		// Clean up event listener
		$(document.body).off('afterChangeItem.ajaxCart.shopLook')
	}
}
</script>
